// =============================================================================
// tasks.ts — Schemas Zod públicos do domínio de tarefas (F15-S03/S05, F32-S02).
//
// FONTE ÚNICA do contrato de tarefas — API e frontend importam DAQUI. Nenhum
// dos dois lados redeclara enums ou o shape de resposta.
//
// Fonte de verdade real é o banco (apps/api/src/db/schema/tasks.ts, F15-S03):
//   - type:   'spc_inclusion' | 'spc_removal' | 'winback' | 'lawyer_handoff' | 'custom'
//   - status: 'open' | 'done' | 'cancelled' — SEM 'in_progress'. O estado
//             "assumida" é derivado de claimedBy IS NOT NULL, nunca um status
//             fantasma. status só muda para 'done'/'cancelled'.
//
// Convenção de payload (ÚNICA em toda a borda HTTP — request e response):
//   - camelCase em todos os campos (assigneeRole, claimedBy, dueAt, ...).
//   - Paginação via limit/offset (não page/per_page).
//   - Datas como string ISO 8601 (UTC, com offset) ou null.
//
// Histórico do drift corrigido em F32-S02: uma versão anterior deste arquivo
// declarava type/status/paginação divergentes de apps/api/.../tasks/schemas.ts,
// e o frontend consumia esse contrato errado sem detectar em teste — o teste
// de contrato validava uma fixture montada à mão, não a resposta real da rota.
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enums (alinhados com db/schema/tasks.ts — F15-S03)
// ---------------------------------------------------------------------------
export const TASK_TYPES = [
    'spc_inclusion',
    'spc_removal',
    'winback',
    'lawyer_handoff',
    'custom',
];
/**
 * Tipo da task.
 * spc_inclusion  — incluir cliente no SPC/Serasa.
 * spc_removal    — remover cliente do SPC/Serasa.
 * winback        — campanha de reativação de cliente inativo.
 * lawyer_handoff — encaminhar caso para advocacia.
 * custom         — tarefa manual, título livre.
 */
export const TaskTypeSchema = z.enum(TASK_TYPES, {
    errorMap: () => ({ message: 'type inválido' }),
});
export const TASK_STATUSES = ['open', 'done', 'cancelled'];
/**
 * Status do ciclo de vida da task. Apenas 3 valores — reflete o enum real do
 * banco. Não existe 'in_progress': uma task assumida continua com
 * status 'open' e claimedBy preenchido.
 */
export const TaskStatusSchema = z.enum(TASK_STATUSES, {
    errorMap: () => ({ message: 'status inválido' }),
});
// ---------------------------------------------------------------------------
// Response (campos da tabela tasks — camelCase, idêntico ao que a rota devolve)
// ---------------------------------------------------------------------------
/**
 * Schema de resposta de uma task — campo a campo alinhado com a tabela `tasks`
 * (F15-S03) e com o shape devolvido por GET/POST /api/tasks*. Sem dados
 * internos de segurança.
 */
export const TaskSchema = z.object({
    /** UUID primário da task. */
    id: z.string().uuid(),
    /** Organização dona da task (multi-tenant). */
    organizationId: z.string().uuid(),
    /**
     * Role key canônica do destinatário (doc 10 §3.1) — não é o user_id, é a
     * role. Qualquer usuário com esse papel pode assumir a tarefa.
     */
    assigneeRole: z.string(),
    /**
     * Cidade à qual a task pertence.
     * null = task válida em qualquer cidade (escopo de org).
     */
    cityId: z.string().uuid().nullable(),
    /** Tipo da task — determina o fluxo de ação. */
    type: TaskTypeSchema,
    /**
     * Tipo da entidade relacionada à task (polimorfismo).
     * Ex: 'customer', 'lead', 'contract'. null = não vinculada a uma entidade.
     */
    entityType: z.string().nullable(),
    /** UUID da entidade relacionada. null quando entityType é null. */
    entityId: z.string().uuid().nullable(),
    /** Título legível da task. Exibido na listagem. */
    title: z.string(),
    /** Descrição detalhada da ação esperada. null = não informada. */
    description: z.string().nullable(),
    /** Prazo para execução, ISO 8601. null = sem prazo definido. */
    dueAt: z.string().nullable().describe('ISO 8601 ou null'),
    /** Status atual da task no seu ciclo de vida. */
    status: TaskStatusSchema,
    /**
     * UUID do usuário que assumiu a task. null = não assumida.
     * "Assumida" (uso humano da UI) é SEMPRE claimedBy !== null — nunca um
     * status derivado.
     */
    claimedBy: z.string().uuid().nullable(),
    /** Timestamp em que a task foi assumida. null = não assumida. */
    claimedAt: z.string().nullable(),
    /** UUID do usuário que concluiu ou cancelou a task. null = não finalizada. */
    completedBy: z.string().uuid().nullable(),
    /** Timestamp em que a task foi concluída ou cancelada. null = não finalizada. */
    completedAt: z.string().nullable(),
    /** Timestamp de criação. */
    createdAt: z.string(),
    /** Timestamp da última atualização. */
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// Create (payload de POST /api/tasks)
// ---------------------------------------------------------------------------
/**
 * Input base para criação de uma task. `assigneeRole` aqui é uma role key
 * livre (string) — a API estreita para o enum canônico de RBAC
 * (apps/api/src/modules/tasks/schemas.ts) via `.extend()`, pois o catálogo de
 * roles é responsabilidade do domínio de auth, não deste pacote.
 * organizationId é injetado pelo service via contexto de autenticação — não
 * consta aqui para evitar bypass de escopo multi-tenant.
 */
export const TaskCreateSchema = z.object({
    /** Role key responsável pela execução (doc 10 §3.1). */
    assigneeRole: z.string().min(1).describe('Role RBAC responsável pela execução'),
    /** Tipo da task. Determina o fluxo de ação e prioridade. */
    type: TaskTypeSchema,
    /** Título exibido na listagem — claro e acionável. */
    title: z.string().min(1).max(255),
    /** Detalhamento da ação esperada. Omitir = sem descrição. */
    description: z.string().max(4000).optional(),
    /** UUID da cidade. Omitir para tarefa global (todas as cidades). */
    cityId: z.string().uuid().optional(),
    /** Tipo da entidade relacionada — ex: lead, customer. */
    entityType: z.string().max(64).optional(),
    /** UUID da entidade relacionada (entityType obrigatório quando preenchido). */
    entityId: z.string().uuid().optional(),
    /** Data/hora limite (ISO 8601 com offset). Omitir para sem prazo. */
    dueAt: z.string().datetime({ offset: true }).optional(),
});
// ---------------------------------------------------------------------------
// List query (GET /api/tasks)
// ---------------------------------------------------------------------------
/**
 * Query params para listagem de tasks. Todos os filtros são opcionais — a
 * ausência retorna todas as tasks visíveis para o usuário dentro do
 * city_scope do JWT. Paginação via limit/offset (não page/per_page).
 */
export const TaskListQuerySchema = z.object({
    /** Filtrar por status. Omitir = todos os status. */
    status: TaskStatusSchema.optional(),
    /** Filtrar por tipo. Omitir = todos os tipos. */
    type: TaskTypeSchema.optional(),
    /** Filtrar tarefas assumidas (claimedBy IS NOT NULL) ou não. Omitir = ambas. */
    claimed: z.enum(['true', 'false']).optional(),
    /** Itens por página (máx 100). */
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /** Deslocamento a partir do primeiro item. */
    offset: z.coerce.number().int().min(0).default(0),
});
// ---------------------------------------------------------------------------
// List response
// ---------------------------------------------------------------------------
/** Resposta paginada da listagem de tasks. */
export const TaskListResponseSchema = z.object({
    data: z.array(TaskSchema),
    total: z.number().int().describe('Total de tasks que atendem ao filtro'),
    limit: z.number().int(),
    offset: z.number().int(),
});
//# sourceMappingURL=tasks.js.map